import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Send, Clock, Package } from "lucide-react";
import { scrollToId } from "@/config";

const projectTypes = [
  { id: "Landing Page", days: 5, desc: "Una pagina, un obiettivo: convertire" },
  { id: "E-commerce", days: 9, desc: "Vendi online con pagamenti integrati" },
  { id: "Sito con Prenotazioni", days: 7, desc: "Appuntamenti e agenda online" },
  { id: "Sito con Menu / Catalogo", days: 7, desc: "Prodotti e servizi in vetrina digitale" },
  { id: "Sito Vetrina", days: 5, desc: "Presenza professionale su Google" },
  { id: "Altro", days: 7, desc: "Raccontamela, la costruiamo insieme" },
];

const extras = [
  "Prenotazioni online",
  "Menu digitale / QR",
  "Pagamenti online",
  "Blog / News",
  "Multilingua",
  "SEO locale avanzata",
];

const urgencies = [
  { id: "Standard", desc: "Tempi di consegna normali" },
  { id: "Express", desc: "Priorità massima, consegna accelerata" },
];

const chipCls = (on) =>
  `rounded-xl border px-4 py-3 text-sm text-left transition-all duration-300 ${
    on
      ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200"
      : "border-white/10 bg-zinc-900/60 text-zinc-300 hover:border-white/25"
  }`;

export const Estimator = () => {
  const [step, setStep] = useState(0);
  const [type, setType] = useState(null);
  const [picked, setPicked] = useState([]);
  const [urgency, setUrgency] = useState(null);

  const estimate = useMemo(() => {
    if (!type) return null;
    const base = projectTypes.find((t) => t.id === type)?.days ?? 10;
    let days = base + picked.length;
    if (urgency === "Express") days = Math.max(base - 2, 4);
    const tier =
      type === "E-commerce" || picked.includes("Pagamenti online")
        ? "E-commerce Custom"
        : (type === "Landing Page" || type === "Sito Vetrina") && picked.length <= 1
        ? "Starter Local"
        : "Pro Business";
    return { tier, from: days, to: days + 4 };
  }, [type, picked, urgency]);

  const toggleExtra = (x) =>
    setPicked((p) => (p.includes(x) ? p.filter((i) => i !== x) : [...p, x]));

  const reset = () => {
    setStep(0);
    setType(null);
    setPicked([]);
    setUrgency(null);
  };

  const sendToForm = () => {
    const msg = `Configurazione dal preventivo guidato — Progetto: ${type}. Extra: ${
      picked.length ? picked.join(", ") : "nessuno"
    }. Urgenza: ${urgency}. Pacchetto suggerito: ${estimate.tier}, tempi stimati ${estimate.from}–${estimate.to} giorni.`;
    window.dispatchEvent(
      new CustomEvent("prefill-quote", { detail: { business_type: type, message: msg } })
    );
    scrollToId("#contatti");
  };

  const steps = [
    {
      title: "Che tipo di sito ti serve?",
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {projectTypes.map((t) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              className={chipCls(type === t.id)}
              data-testid={`estimator-type-${t.id.toLowerCase().replace(/[\s/]+/g, "-")}`}
            >
              <span className="block font-semibold">{t.id}</span>
              <span className="mt-1 block text-xs text-zinc-500">{t.desc}</span>
            </button>
          ))}
        </div>
      ),
      canNext: !!type,
    },
    {
      title: "Cosa non può mancare?",
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {extras.map((x) => (
            <button
              key={x}
              onClick={() => toggleExtra(x)}
              className={chipCls(picked.includes(x))}
              data-testid={`estimator-extra-${x.toLowerCase().replace(/[\s/]+/g, "-")}`}
            >
              {x}
            </button>
          ))}
        </div>
      ),
      canNext: true,
    },
    {
      title: "Quanta fretta hai?",
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {urgencies.map((u) => (
            <button
              key={u.id}
              onClick={() => setUrgency(u.id)}
              className={chipCls(urgency === u.id)}
              data-testid={`estimator-urgency-${u.id.toLowerCase()}`}
            >
              <span className="block font-semibold">{u.id}</span>
              <span className="mt-1 block text-xs text-zinc-500">{u.desc}</span>
            </button>
          ))}
        </div>
      ),
      canNext: !!urgency,
    },
  ];

  const done = step >= steps.length;

  return (
    <section id="preventivo" className="relative py-24 lg:py-32 bg-[#0F0F14]" data-testid="estimator-section">
      <div className="mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-2xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-4">Preventivo Guidato</p>
          <h2 className="font-syne text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Configura il tuo sito in 30 secondi.{" "}
            <span className="text-zinc-500">Scopri pacchetto e tempi.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-3 rounded-2xl border border-white/10 bg-zinc-900/60 backdrop-blur-md p-8"
          >
            {!done ? (
              <>
                <p className="font-mono text-xs text-zinc-500 mb-2">
                  Passo {step + 1} / {steps.length}
                </p>
                <h3 className="font-syne text-xl font-semibold mb-6">{steps[step].title}</h3>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {steps[step].content}
                  </motion.div>
                </AnimatePresence>
                <div className="mt-8 flex items-center justify-between">
                  <button
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100 disabled:opacity-30 transition-colors"
                    data-testid="estimator-back"
                  >
                    <ArrowLeft size={16} />
                    Indietro
                  </button>
                  <button
                    onClick={() => steps[step].canNext && setStep((s) => s + 1)}
                    disabled={!steps[step].canNext}
                    className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-6 py-2.5 text-sm font-semibold text-zinc-950 transition-all duration-300 hover:bg-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="estimator-next"
                  >
                    {step === steps.length - 1 ? "Vedi la stima" : "Avanti"}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid="estimator-result"
              >
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-6">La tua stima</p>
                <div className="flex flex-wrap gap-8">
                  <div>
                    <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono uppercase tracking-widest mb-2">
                      <Package size={14} /> Pacchetto
                    </div>
                    <p className="font-syne text-3xl font-extrabold bg-gradient-to-r from-cyan-400 to-indigo-400 bg-clip-text text-transparent">
                      {estimate.tier}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono uppercase tracking-widest mb-2">
                      <Clock size={14} /> Consegna
                    </div>
                    <p className="font-syne text-3xl font-extrabold">
                      {estimate.from}–{estimate.to} <span className="text-lg text-zinc-500">giorni</span>
                    </p>
                  </div>
                </div>
                <p className="mt-6 text-sm text-zinc-400 leading-relaxed">
                  Il prezzo finale dipende dai dettagli del progetto: inviami la configurazione e ricevi un preventivo
                  preciso entro 24 ore, gratis e senza impegno.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    onClick={sendToForm}
                    className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3 font-semibold text-zinc-950 transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(56,189,248,0.4)]"
                    data-testid="estimator-send-button"
                  >
                    <Send size={16} />
                    Invia e ricevi il preventivo
                  </button>
                  <button
                    onClick={reset}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm text-zinc-300 transition-all duration-300 hover:border-cyan-400/50 hover:text-cyan-300"
                    data-testid="estimator-restart"
                  >
                    <RotateCcw size={15} />
                    Ricomincia
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-2 rounded-2xl border border-white/10 bg-zinc-900/40 p-8"
            data-testid="estimator-summary"
          >
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">Riepilogo live</p>
            <dl className="space-y-5 text-sm">
              <div>
                <dt className="text-zinc-500 mb-1">Progetto</dt>
                <dd className="font-semibold text-zinc-100">{type ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-zinc-500 mb-1">Extra</dt>
                <dd className="text-zinc-300">{picked.length ? picked.join(" · ") : "—"}</dd>
              </div>
              <div>
                <dt className="text-zinc-500 mb-1">Urgenza</dt>
                <dd className="text-zinc-300">{urgency ?? "—"}</dd>
              </div>
              <div className="border-t border-white/10 pt-5">
                <dt className="text-zinc-500 mb-1">Stima attuale</dt>
                <dd className="font-syne text-lg font-bold text-cyan-400">
                  {estimate ? `${estimate.tier} · ${estimate.from}–${estimate.to} giorni` : "Seleziona un tipo di sito"}
                </dd>
              </div>
            </dl>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};
